import React, { useState, useRef, useEffect } from 'react';
import './Chatbot.css';

const SYSTEM_PROMPT = `Tu es l'assistant virtuel de SACEM Industries. Réponds toujours dans la langue de l'utilisateur (français ou anglais). Sois professionnel, concis et utile. Si une question dépasse tes connaissances sur SACEM, oriente l'utilisateur vers la page Contact du site.

## SACEM Industries — Vue d'ensemble
- Leader tunisien dans la fabrication et la distribution de transformateurs électriques
- Fondée en 1966, siège social à Menzel Bourguiba-Tinja, Tunisie
- Usine de 6 hectares, 18 000 m² de surface couverte
- 450 employés qualifiés
- Plus de 10 000 transformateurs fabriqués
- Capacité de production : 2 000 MVA/an
- Exportation vers plus de 30 pays

## Groupe SACEM
- **SACEM Industries** : Fabrication de transformateurs monophasés, triphasés et spéciaux
- **SACEM Service** : Service après-vente réactif, maintenance et réparation de transformateurs
- **SACEM Training** : Premier centre africain de formation sur les transformateurs (50+ ans d'expertise, programmes conformes aux normes internationales)
- **SACEM Power** : Fabrication de tableaux électriques MT/BT et cellules

## Produits

### Transformateurs de Distribution Triphasés (≤24 kV et ≤36 kV)
- Immergés dans l'huile, remplissage hermétique ou respirant
- Refroidissement naturel ONAN
- Installation intérieure/extérieure
- Couleur : gris clair RAL 7035
- Conformes CEI 60076 | Homologués CESI, KEMA, VEIKI

### Transformateurs de Puissance Triphasés
- Immergés dans l'huile, refroidissement ONAN/ONAF
- Installation intérieure/extérieure
- Conformes CEI 60076 | Haute efficacité, fiabilité à long terme

### Transformateurs Monophasés
- Immergés dans l'huile, refroidissement ONAN
- Protection anticorrosion C5H
- Couleur : RAL 7035 (autres couleurs sur demande)
- Conformes CEI 60076

### Transformateurs Spéciaux (sur mesure)
- Transformateurs de mise à la terre, autotransformateurs, transformateurs d'isolement
- Conçus pour des applications spécifiques par une équipe expérimentée

### Power Systems (SACEM Power)
- Tableaux et cellules MT/BT
- Coffrets de protection transformateur
- Filtres actifs, batteries de condensateurs
- Tableaux AIS

## Services
- **SAV & Réparation** : Maintenance préventive et corrective, intervention rapide
- **Formation** : Programmes certifiés sur les transformateurs, premier centre africain du genre
- **Export** : Présence dans 30+ pays

## Certifications
- ISO 9001 (Management de la qualité)
- ISO 14001 (Management environnemental)
- ISO 45001 (Santé et sécurité au travail)
- Produits homologués : CESI, KEMA, VEIKI

## Contact & Pages du site
- Page Contact : formulaire en ligne pour toute demande
- FAQ : questions fréquentes sur les produits et services
- Actualités : dernières nouvelles de SACEM Industries
- Offres d'emploi disponibles sur la page Carrières

Réponds de façon claire et professionnelle. Pour des devis ou informations techniques très spécifiques, recommande de remplir le formulaire de contact sur le site.`;

const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_MODEL = 'llama-3.3-70b-versatile';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content:
    'Bonjour ! Je suis l\'assistant virtuel de SACEM Industries. Comment puis-je vous aider ?\n\nHello! I\'m SACEM Industries\' virtual assistant. How can I help you?',
};

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMessage = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(GROQ_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.REACT_APP_GROQ_API_KEY}`,
        },
        body: JSON.stringify({
          model: GROQ_MODEL,
          messages: [
            { role: 'system', content: SYSTEM_PROMPT },
            ...newMessages,
          ],
          max_tokens: 600,
          temperature: 0.7,
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const reply = data.choices?.[0]?.message?.content ?? '';
      setMessages([...newMessages, { role: 'assistant', content: reply }]);
    } catch {
      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content:
            'Désolé, une erreur est survenue. Veuillez réessayer.\nSorry, an error occurred. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="cb-container">
      {isOpen && (
        <div className="cb-window" role="dialog" aria-label="Assistant SACEM">
          <div className="cb-header">
            <div className="cb-header-info">
              <div className="cb-avatar" aria-hidden="true">S</div>
              <div className="cb-header-text">
                <span className="cb-title">Assistant SACEM</span>
                <span className="cb-status">
                  <span className="cb-dot" aria-hidden="true" />
                  En ligne
                </span>
              </div>
            </div>
            <button
              className="cb-close"
              onClick={handleToggle}
              aria-label="Fermer le chat"
            >
              ✕
            </button>
          </div>

          <div className="cb-messages" role="log" aria-live="polite">
            <div className="cb-msg cb-msg--assistant">
              <p className="cb-msg-text">{WELCOME_MESSAGE.content}</p>
            </div>

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`cb-msg cb-msg--${msg.role}`}
              >
                <p className="cb-msg-text">{msg.content}</p>
              </div>
            ))}

            {isLoading && (
              <div className="cb-msg cb-msg--assistant cb-typing" aria-label="En train d'écrire">
                <span /><span /><span />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="cb-input-area">
            <textarea
              ref={inputRef}
              className="cb-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Votre message… / Your message…"
              rows={1}
              disabled={isLoading}
              aria-label="Saisir un message"
            />
            <button
              className="cb-send"
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
              aria-label="Envoyer"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <button
        className={`cb-toggle ${isOpen ? 'cb-toggle--open' : ''}`}
        onClick={handleToggle}
        aria-label={isOpen ? 'Fermer le chat' : 'Ouvrir le chat'}
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>
        )}
      </button>
    </div>
  );
}
